// BookiesSafetyActions.jsx -- the "⋯" options for a booth or a booth member (#1421 spec section 4 steps 5-7, plan
// Task 12). Single job: let the user leave or report a booth, and report, block or unblock a member, then say what
// happened. Presentational: bookiesSafety owns every write and its Sentry warning; RLS and report_booth own the rules.
// Booth scope: ({ user, group, onChanged, limited }). Member scope: ({ user, group, memberId, blocked, onChanged }).
// onChanged gets the confirmed result: { userId } after a leave, { userId, memberId, blocked } after a block or unblock.
// `limited` (no Bookies consent yet, plan E10) keeps Leave and Report only. `canBlock` is false until the server
// enforces blocks (PR 2b; the page knows from the board's `sharing` key), so Block is never promised early.
import { useId, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { JT } from '../../components/ui/jumbotronTokens';
import { REPORT_REASONS, leaveBooth, reportBooth, setBlocked } from './bookiesSafety';
import { accessDeniedMessage } from "../../lib/accessCopy";

const row = { display: 'flex', alignItems: 'center', width: '100%', minHeight: 44, padding: '0 12px', background: 'none', border: 'none', borderRadius: 8, color: JT.text.primary, fontSize: 13, fontWeight: 700, fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' };
const smallBtn = { minHeight: 44, borderRadius: 8, padding: '0 14px', fontSize: 13, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' };

const REPORT_MSG = {
  ST023: "You've sent a lot of reports today. Try again tomorrow.",
  // #1503 PR 2: report_booth refuses an account that is no longer approved (ST025).
  ST025: accessDeniedMessage("revoked"),
};

export default function BookiesSafetyActions({ user, group, memberId = null, blocked = false, onChanged, limited = false, canBlock = true }) {
  const isMember = memberId != null;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('menu');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  const inFlight = useRef(false);

  const say = (text, tone = 'ok') => setMsg({ text, tone });

  async function run(fn) {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setMsg(null);
    try { await fn(); } finally { inFlight.current = false; setBusy(false); }
  }

  const doLeave = () => run(async () => {
    const r = await leaveBooth(supabase, user.id, group.id);
    if (!r.ok) { say("Couldn't leave the booth. Try again.", 'err'); return; }
    say('You left the booth');
    setView('menu');
    onChanged?.({ userId: user.id });
  });

  const doReport = () => run(async () => {
    const trimmed = note.trim();
    const r = await reportBooth(supabase, { groupId: group.id, userId: isMember ? memberId : null, reason, note: trimmed || null });
    if (!r.ok) { say(REPORT_MSG[r.code] || "Couldn't send the report. Try again.", 'err'); return; }
    say("Thanks. We'll review it.");
    setView('menu'); setReason(null); setNote('');
  });

  const doBlock = () => run(async () => {
    const r = await setBlocked(supabase, user.id, memberId, !blocked);
    if (!r.ok) {
      if (!blocked && r.code === 'ST024') say("You've blocked the most members you can.", 'err');
      else say(blocked ? "Couldn't unblock. Try again." : "Couldn't block. Try again.", 'err');
      return;
    }
    say(blocked ? 'Member unblocked' : 'Member blocked');
    onChanged?.({ userId: user.id, memberId, blocked: !blocked });
  });

  const label = isMember ? 'Member options' : 'Booth options';
  // React-generated, never data-derived: Sentry click breadcrumbs serialise ids and names (pre-Codex Sec F1).
  const panelId = `bookies-actions-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" aria-label={label} aria-expanded={open} aria-controls={panelId}
        onClick={() => { setOpen((v) => !v); setView('menu'); setMsg(null); }}
        style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: JT.text.tertiary, fontSize: 18, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1 }}>⋯</button>
      {open && (
        <div id={panelId} role="group" aria-label={label}
          style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, width: 272, maxWidth: 'calc(100vw - 48px)', background: JT.bg.card, border: `1px solid ${JT.border.mid}`, borderRadius: 12, padding: 6, boxShadow: '0 12px 32px rgba(2,6,23,.55)' }}>
          {view === 'menu' && (
            <>
              {!isMember && (
                <button type="button" style={row} disabled={busy} onClick={() => { setView('confirmLeave'); setMsg(null); }}>
                  Leave booth
                </button>
              )}
              <button type="button" style={row} disabled={busy} onClick={() => { setView('report'); setMsg(null); }}>
                {isMember ? 'Report member' : 'Report booth'}
              </button>
              {isMember && !limited && canBlock && (
                <button type="button" style={{ ...row, color: blocked ? JT.text.primary : JT.status.lost.color }} disabled={busy} onClick={doBlock}>
                  {blocked ? 'Unblock' : 'Block'}
                </button>
              )}
            </>
          )}
          {view === 'confirmLeave' && (
            <div style={{ padding: '8px 10px' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: JT.text.primary, marginBottom: 4 }}>{`Leave ${group.name}?`}</div>
              <div style={{ fontSize: 12, color: JT.text.secondary, lineHeight: 1.4, marginBottom: 10 }}>You'll need the invite code to rejoin.</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={doLeave} disabled={busy} style={{ ...smallBtn, flex: 1, border: 'none', background: JT.status.lost.color, color: JT.bg.root, opacity: busy ? 0.6 : 1 }}>{busy ? 'Leaving…' : 'Leave'}</button>
                <button type="button" onClick={() => setView('menu')} disabled={busy} style={{ ...smallBtn, background: 'none', border: `1px solid ${JT.border.mid}`, color: JT.text.secondary }}>Cancel</button>
              </div>
            </div>
          )}
          {view === 'report' && (
            <fieldset style={{ border: 'none', margin: 0, padding: '6px 6px 8px' }}>
              <legend style={{ fontSize: 13, fontWeight: 800, color: JT.text.primary, padding: '0 4px 6px' }}>{isMember ? 'Why are you reporting this member?' : 'Why are you reporting this booth?'}</legend>
              {REPORT_REASONS.map(([key, text]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '0 6px', fontSize: 13, color: JT.text.secondary, cursor: 'pointer' }}>
                  <input type="radio" name={`${panelId}-reason`} value={key} checked={reason === key} onChange={() => setReason(key)} disabled={busy} />
                  {text}
                </label>
              ))}
              <textarea aria-label="Add details (optional)" placeholder="Add details (optional)" maxLength={500} rows={3} value={note} onChange={(e) => setNote(e.target.value)} disabled={busy}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, background: JT.bg.root, border: `1px solid ${JT.border.mid}`, borderRadius: 8, padding: '8px 10px', color: JT.text.primary, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" onClick={doReport} disabled={busy || !reason} style={{ ...smallBtn, flex: 1, border: 'none', background: JT.brand.cta, color: JT.text.primary, opacity: (busy || !reason) ? 0.5 : 1 }}>{busy ? 'Sending…' : 'Send report'}</button>
                <button type="button" onClick={() => setView('menu')} disabled={busy} style={{ ...smallBtn, background: 'none', border: `1px solid ${JT.border.mid}`, color: JT.text.secondary }}>Cancel</button>
              </div>
            </fieldset>
          )}
          {msg && (
            <div role={msg.tone === 'err' ? 'alert' : 'status'} style={{ fontSize: 12, color: msg.tone === 'err' ? JT.status.lost.color : JT.status.won.color, padding: '6px 10px 4px' }}>{msg.text}</div>
          )}
        </div>
      )}
    </div>
  );
}
